import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { message } from 'antd';
import moment from 'moment';
import Layout from '../../../components/Layout';
import Loader from '../../../components/Loader';
import { post } from '../../../services/ApiService';
import './catdReplies.css';

const PAGE_SIZES = [10, 20, 50, 100];
const COLUMN_COUNT = 9;

const PLATFORMS = [
  { value: 'All', label: 'All platforms' },
  { value: 'Ebay', label: 'eBay' },
  { value: 'Amazon', label: 'Amazon' },
  { value: 'GroupOn', label: 'Groupon' },
];

const STAGES = [
  { value: 'all', label: 'All', count: 'all' },
  { value: 'replied', label: 'Replied', count: 'replied' },
  { value: 'none', label: 'No reply', count: 'noReply' },
];

function londonDate(offsetDays) {
  return moment().subtract(offsetDays, 'days').format('YYYY-MM-DD');
}

function pageWindow(current, last) {
  const pages = [];
  for (let page = 1; page <= last; page += 1) {
    if (page === 1 || page === last || Math.abs(page - current) <= 1) pages.push(page);
    else if (pages[pages.length - 1] !== null) pages.push(null);
  }
  return pages;
}

const shortDateTime = (value) => (value ? moment(value).format('D MMM YYYY, HH:mm') : null);

function windowLabel(row) {
  if (row.windowEndReason === 'dispatch') return `Dispatch ${shortDateTime(row.windowEnd)}`;
  if (row.windowEndReason === 'next_order') {
    const when = shortDateTime(row.windowEnd);
    return row.nextOrderId ? `Next order ${row.nextOrderId} · ${when}` : `Next order ${when}`;
  }
  return 'Still open';
}

const CatdReplyReport = () => {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState({
    from: londonDate(29),
    to: londonDate(0),
    platform: 'All',
    reply: 'all',
    search: '',
    page: 1,
    limit: 20,
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handle = setTimeout(() => {
      setQuery((current) => (
        current.search === searchInput ? current : { ...current, search: searchInput, page: 1 }
      ));
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    post('api/orders/report', query)
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows || []);
        setSummary(res.summary || null);
        setTotal(res.total || 0);
      })
      .catch((error) => {
        if (cancelled || error?.handledByInterceptor) return;
        message.error(error?.response?.data?.error || 'Could not load the report.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [query]);

  const totalPages = Math.max(Math.ceil(total / query.limit), 1);
  const firstRow = total === 0 ? 0 : (query.page - 1) * query.limit + 1;
  const lastRow = Math.min(query.page * query.limit, total);
  const filtered = query.search || query.platform !== 'All' || query.reply !== 'all';

  return (
    <Layout>
      <div className="catd-page">
        <div className="catd-head">
          <h2>Reports</h2>
          <span>Orders that sent the greeting. A reply is a buyer message after the order was created and before dispatch or that customer’s next order.</span>
        </div>

        <div className="catd-stages" role="group" aria-label="Filter by reply">
          {STAGES.map((stage) => (
            <button
              key={stage.value}
              type="button"
              aria-pressed={query.reply === stage.value}
              className={`catd-stage ${query.reply === stage.value ? 'is-active' : ''}`}
              onClick={() => setQuery((current) => ({ ...current, reply: stage.value, page: 1 }))}
            >
              <strong>{summary ? summary[stage.count] : '–'}</strong>
              {stage.label}
            </button>
          ))}
        </div>

        <div className="catd-tools">
          <div className="catd-search">
            <i className="fas fa-search"></i>
            <input
              type="search"
              placeholder="Search order or customer"
              aria-label="Search order or customer"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <div className="catd-filters">
            <label htmlFor="catd-from">From</label>
            <input
              id="catd-from"
              className="catd-date"
              type="date"
              value={query.from}
              max={query.to}
              onChange={(event) => {
                if (!event.target.value) return;
                setQuery((current) => ({ ...current, from: event.target.value, page: 1 }));
              }}
            />
            <label htmlFor="catd-to">To</label>
            <input
              id="catd-to"
              className="catd-date"
              type="date"
              value={query.to}
              min={query.from}
              onChange={(event) => {
                if (!event.target.value) return;
                setQuery((current) => ({ ...current, to: event.target.value, page: 1 }));
              }}
            />
            <select
              className="catd-select"
              aria-label="Filter by platform"
              value={query.platform}
              onChange={(event) => setQuery((current) => ({ ...current, platform: event.target.value, page: 1 }))}
            >
              {PLATFORMS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <label htmlFor="catd-page-size">Rows</label>
            <select
              id="catd-page-size"
              className="catd-select is-narrow"
              value={query.limit}
              onChange={(event) => setQuery((current) => ({ ...current, limit: Number(event.target.value), page: 1 }))}
            >
              {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </div>
        </div>

        <div className="catd-card">
          <table className="catd-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Platform</th>
                <th>Status</th>
                <th>Created</th>
                <th>Dispatched</th>
                <th>Window closes</th>
                <th>Reply</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={COLUMN_COUNT} className="catd-empty"><Loader /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="catd-empty">
                    {filtered
                      ? 'No orders match these filters.'
                      : 'No orders with a greeting in this date range.'}
                  </td>
                </tr>
              )}
              {!loading && rows.map((row) => (
                <tr key={row.id}>
                  <td className="catd-order">
                    <Link to={`/orders/${row.id}`}>{row.orderId}</Link>
                  </td>
                  <td>
                    <div>{row.buyerName || row.buyerUsername || '—'}</div>
                    {row.buyerName && row.buyerUsername && (
                      <div className="catd-muted">{row.buyerUsername}</div>
                    )}
                  </td>
                  <td>
                    <span className={`catd-platform is-${String(row.platform || '').toLowerCase()}`}>
                      {row.platform || '—'}
                    </span>
                  </td>
                  <td className="catd-nowrap">{row.status || '—'}</td>
                  <td className="catd-nowrap">{shortDateTime(row.createdAt) || '—'}</td>
                  <td className="catd-nowrap">{shortDateTime(row.shippingDate) || '—'}</td>
                  <td className="catd-window">{windowLabel(row)}</td>
                  <td>
                    {row.replied ? (
                      <span className="catd-status is-done" title={shortDateTime(row.replyAt) || undefined}>
                        Replied {shortDateTime(row.replyAt)}
                      </span>
                    ) : (
                      <span className="catd-status is-idle">No reply</span>
                    )}
                  </td>
                  <td>
                    {row.replyPreview
                      ? <div className="catd-clip" title={row.replyPreview}>{row.replyPreview}</div>
                      : <span className="catd-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="catd-foot">
            <span>{loading ? 'Loading…' : `Showing ${firstRow}–${lastRow} of ${total}`}</span>
            <nav className="catd-pager" aria-label="Pages">
              <button
                type="button"
                className="catd-page-btn"
                disabled={query.page <= 1}
                onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
              >
                Previous
              </button>
              {pageWindow(query.page, totalPages).map((page, index) => (
                page === null ? (
                  <span key={`gap-${index}`} aria-hidden="true">…</span>
                ) : (
                  <button
                    key={page}
                    type="button"
                    aria-current={page === query.page ? 'page' : undefined}
                    className={`catd-page-btn ${page === query.page ? 'is-current' : ''}`}
                    onClick={() => setQuery((current) => ({ ...current, page }))}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                type="button"
                className="catd-page-btn"
                disabled={query.page >= totalPages}
                onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CatdReplyReport;
